import { computed } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type { UseReviewTooltipOptions, UseReviewTooltipReturn } from './types'

import {
  formatReviewTooltip,
  formatReviewUsersList,
} from '../utils/format-preparation-and-review'

/**
 * Composable for generating review tooltip content
 * Used by workflow-review-checkmark and workflow-review-status-block
 */
export function useReviewTooltip(
  options: UseReviewTooltipOptions
): UseReviewTooltipReturn {
  const { reviews, hideTooltip } = options
  const I18n = useI18n()

  const upToDateReviews = computed(() =>
    reviews.value.filter((review) => review.outdatedAt === null)
  )

  const reviewed = computed(() => reviews.value && reviews.value.length > 0)

  const hasAnyUpToDateReview = computed(
    () => reviewed.value && upToDateReviews.value.length > 0
  )

  /**
   * Simple tooltip for checkmarks
   */
  const simpleTooltip = computed(() => {
    const shouldHideTooltip =
      hideTooltip?.value === true || hideTooltip?.value === 'true'
    if (shouldHideTooltip || !reviewed.value) return ''

    const prefix = I18n.t('reconciliation_review.reviewed_by')
    const users = formatReviewUsersList(upToDateReviews.value)

    return `${prefix}<br />${users}`
  })

  /**
   * Detailed tooltip for review status block (handles outdated reviews)
   */
  const detailedTooltip = computed(() => {
    if (!reviewed.value) return ''

    return formatReviewTooltip(reviews.value, hasAnyUpToDateReview.value)
  })

  return {
    upToDateReviews,
    reviewed,
    hasAnyUpToDateReview,
    simpleTooltip,
    detailedTooltip,
  }
}
